/**
 * One tiffin order: where it is, and everything in it.
 *
 * The timeline is the server's, named for this order's route — "Out for
 * Delivery" and "Delivered" for pick & drop, "Ready for Pickup" and
 * "Collected" for pickup. A cancelled or refunded order gets a banner rather
 * than a timeline that silently stops.
 */

import { StyleSheet, View } from 'react-native';

import {
  formatDayRange,
  formatDateTime,
  formatDays,
  formatMeals,
  formatMoney,
  formatPeople,
  formatWindow,
  orderStatusMeta,
  serviceScheduleLabel,
  tiffinFulfilmentLabel,
} from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Badge } from '../../components/common/Badge';
import { Card, CardDivider, CardRow } from '../../components/common/Card';
import { ErrorState } from '../../components/feedback/States';
import { Glyph } from '../../components/common/Icon';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Skeleton } from '../../components/feedback/Skeleton';
import { useScheduleDays } from '../../components/tiffin/SchedulePicker';
import { SupportCard } from '../../components/layout/SupportCard';
import { Text } from '../../components/common/Text';
import { TrackingTimeline } from '../../components/donation/TrackingTimeline';
import { useAppConfig, useTiffinOrder } from '../../core/api/hooks';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

export function TiffinOrderScreen({ navigation, route }: DonorScreenProps<'TiffinOrder'>) {
  const t = useT();
  const scheduleDays = useScheduleDays();
  const pick = useLocalized();
  const { orderId } = route.params;
  const query = useTiffinOrder(orderId);
  const { data: config } = useAppConfig();

  if (query.isError) {
    return (
      <ScreenShell leading="back" onBackPress={navigation.goBack} testID="screen-tiffin-order">
        <ErrorState
          title={t('Order unavailable')}
          message={t('We could not load this order.')}
          onRetry={() => void query.refetch()}
        />
      </ScreenShell>
    );
  }

  const order = query.data;
  const window = config?.tiffin?.slots.find((slot) => slot.slot === order?.serviceSlot);
  const delivery = order?.fulfilmentType === 'DOORSTEP_DELIVERY';

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      onRefresh={() => void query.refetch()}
      refreshing={query.isRefetching}
      testID="screen-tiffin-order"
    >
      {query.isLoading || !order ? (
        <View style={styles.loading}>
          <Skeleton height={32} width="60%" />
          <Skeleton height={20} width="40%" />
          <Skeleton height={280} />
        </View>
      ) : (
        <>
          <View style={styles.headerRow}>
            <Text variant="screenTitle" testID="tiffin-order-reference">
              #{order.publicReference}
            </Text>
            <Badge
              label={order.statusLabel}
              tone={orderStatusMeta[order.orderStatus].tone}
              testID="tiffin-order-status"
            />
          </View>

          <Text variant="cardTitle" style={styles.meal}>
            {`${t(order.serviceLabel)}, ${formatDayRange(order.serviceDate, order.endDate)}`}
          </Text>
          {window ? (
            // Digits and AM/PM alone would align left in Urdu, away from the
            // title above them; they follow the screen's direction instead.
            <Text
              variant="body"
              tone="secondary"
              align="start"
              testID="tiffin-order-window"
            >
              {formatWindow(window.start, window.end)}
            </Text>
          ) : null}
          <Text variant="body" tone="secondary" testID="tiffin-order-schedule">
            {`${t(serviceScheduleLabel[order.schedule])} · ${scheduleDays(order.schedule, order.serviceDate)} · ${formatMeals(order.deliveries)}`}
          </Text>

          <View style={styles.metaRow}>
            <Meta icon="people" value={formatPeople(order.persons)} />
            <Meta icon="calendar" value={formatDays(order.days)} />
            <Meta icon="price" value={formatMoney(order.totalAmount)} />
            <Meta
              icon={delivery ? 'delivery' : 'pickup'}
              value={t(tiffinFulfilmentLabel[order.fulfilmentType])}
            />
          </View>

          {order.exception ? (
            <View style={styles.exception} accessibilityLiveRegion="polite" testID="tiffin-order-exception">
              <Text variant="bodyStrong" tone="danger">{t(order.exception.label)}</Text>
              <Text variant="caption" tone="secondary">{t(order.exception.message)}</Text>
            </View>
          ) : null}

          <Card title={t('Order Progress')} testID="tiffin-order-progress">
            <TrackingTimeline steps={order.steps} />
          </Card>

          <Card title={t('In each box')} style={styles.card} testID="tiffin-order-items">
            {order.items.map((line, index) => (
              <View key={`${line.menuItemId ?? line.name}-${index}`} style={styles.line}>
                <Text variant="body" style={styles.lineName}>
                  {pick(line.name, line.nameUr)}
                  <Text variant="body" tone="secondary">{`  ×${line.quantity}`}</Text>
                </Text>
                <Text variant="body">{formatMoney(line.lineTotal)}</Text>
              </View>
            ))}
            <CardDivider />
            <CardRow label={t('One box')} value={formatMoney(order.boxTotal)} />
            <CardRow
              label={`× ${formatPeople(order.persons)} × ${formatMeals(order.deliveries)}`}
              value={formatMoney(order.subtotal)}
            />
            {delivery ? (
              <CardRow
                label={`${t('Pick & drop')} × ${formatMeals(order.deliveries)}`}
                value={formatMoney(order.deliveryFee)}
              />
            ) : null}
            <CardRow label={t('Total')} value={formatMoney(order.totalAmount)} emphasis />
          </Card>

          <Card
            title={t(tiffinFulfilmentLabel[order.fulfilmentType])}
            style={styles.card}
            testID="tiffin-order-route"
          >
            {delivery && order.address ? (
              <CardRow
                label={t('Deliver to')}
                value={[order.address.addressLine, order.address.area, order.address.city, order.address.landmark]
                  .filter(Boolean)
                  .join(', ')}
              />
            ) : (
              <CardRow label={t('Collect from')} value={order.pickupAddress ?? '—'} />
            )}
            <CardRow label={t('Name')} value={order.customerName ?? '—'} />
            <CardRow label={t('Mobile')} value={order.customerMobile ?? '—'} />
            {order.note ? <CardRow label={t('Note')} value={order.note} /> : null}
            <CardRow label={t('Placed')} value={formatDateTime(order.createdAt)} />
          </Card>

          <View style={styles.notice}>
            <Glyph name="privacy" size={18} />
            <Text variant="caption" tone="secondary" style={styles.noticeText}>
              {t('Pull down to refresh. We update this order at every step.')}
            </Text>
          </View>

          <SupportCard
            support={config?.support}
            title={t('Need help with this order?')}
            reference={order.publicReference}
          />
        </>
      )}
    </ScreenShell>
  );
}

function Meta({ icon, value }: { icon: string; value: string }) {
  return (
    <View style={styles.meta}>
      <Glyph name={icon} size={14} />
      <Text variant="caption" tone="secondary">
        {value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  loading: {
    gap: space.md,
  },
  headerRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.sm,
  },
  meal: {
    marginTop: space.xs,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: space.lg,
    marginTop: space.md,
    marginBottom: space.lg,
  },
  meta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
  },
  exception: {
    padding: space.md,
    borderRadius: radius.md,
    backgroundColor: colors.action.dangerSubtle,
    gap: space.xxs,
    marginBottom: space.lg,
  },
  card: {
    marginTop: space.lg,
  },
  line: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: space.md,
    paddingVertical: space.xs,
  },
  lineName: {
    flex: 1,
  },
  notice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    marginTop: space.lg,
    marginBottom: space.lg,
    padding: space.md,
    borderRadius: radius.md,
    backgroundColor: colors.bg.brandSubtle,
  },
  noticeText: {
    flex: 1,
  },
});
